import { useEffect, useState } from "react";
import { listZones } from "../api/rest";
import zonesJson from "../../../infra/seed/zones.json";

/**
 * Where this warehouse's floor actually is, in world metres.
 *
 * ADR 143 moved the zone SHAPES off the bundled demo seed and onto the zones
 * API; ADR 204 did the same for the camera, after it spent months aiming at the
 * seed's 60x40 centre. The heat field was the last consumer still sizing itself
 * from `infra/seed/zones.json` at build time, which is why the heatmap renders
 * blank on every site that is not the demo: wh-webinar's floor sits at
 * x 112..414, y -386..-87, and a grid pinned to (0,0)x(60,40) drops all of it.
 *
 * The frame comes from the same zones the scene draws, so the heat can never
 * disagree with the floor under it. Zones are the right authority rather than
 * live poses: it must be known BEFORE any pose arrives, or the first minutes of
 * dwell land in a grid that is about to be replaced.
 */
export interface FloorFrame {
  /** World x of the frame's low corner. */
  originX: number;
  /** World y (scene z) of the frame's low corner. */
  originY: number;
  widthM: number;
  heightM: number;
}

/** Grown past the zones so traffic in the aisle mouths and docks is not clipped. */
const MARGIN = 0.08;

export const DEMO_FRAME: FloorFrame = {
  originX: 0,
  originY: 0,
  widthM: zonesJson.widthM ?? 60,
  heightM: zonesJson.heightM ?? 40,
};

export function frameFromPolygons(
  polygons: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>,
): FloorFrame | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const poly of polygons) {
    for (const pt of poly) {
      const [x, y] = pt;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

  // A degenerate footprint (one zone, or a site mid-import) would otherwise give
  // a zero-width grid and divide by zero downstream.
  const contentW = Math.max(maxX - minX, 1);
  const contentH = Math.max(maxY - minY, 1);
  const padX = contentW * MARGIN;
  const padY = contentH * MARGIN;
  return {
    originX: minX - padX,
    originY: minY - padY,
    widthM: contentW + padX * 2,
    heightM: contentH + padY * 2,
  };
}

// One in-flight fetch per warehouse, shared by every caller. The heat field is
// read by two components that must agree on the frame down to the metre --
// disagreeing would paint the texture somewhere the splats never land -- and a
// second uncoordinated fetch is the easy way to end up with two frames.
const cache = new Map<string, Promise<FloorFrame>>();

export function loadFloorFrame(siteId: string, warehouseId: string): Promise<FloorFrame> {
  const key = `${siteId}:${warehouseId}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = listZones(siteId, warehouseId)
    .then((rows) => {
      const polys = rows.map((r) => r.polygon).filter((p): p is number[][] => !!p && p.length > 0);
      return frameFromPolygons(polys) ?? DEMO_FRAME;
    })
    // A site with no zones yet still gets a floor, and the demo keeps working
    // offline. Cached either way: retrying per render would hammer the API.
    .catch(() => DEMO_FRAME);
  cache.set(key, p);
  return p;
}

/** Test seam — the module cache would otherwise leak between cases. */
export function clearFloorFrameCache(): void {
  cache.clear();
}

export function useFloorFrame(
  siteId: string | undefined, warehouseId: string | undefined,
): FloorFrame {
  const [frame, setFrame] = useState<FloorFrame>(DEMO_FRAME);
  useEffect(() => {
    if (!siteId || !warehouseId) return;
    let cancelled = false;
    loadFloorFrame(siteId, warehouseId).then((f) => { if (!cancelled) setFrame(f); });
    return () => { cancelled = true; };
  }, [siteId, warehouseId]);
  return frame;
}
